import { StateControlAbstraction } from "../../state-control.abstraction";
import { signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import {
  dayGranularity,
  GranularityDefaults,
  GranularityOptions,
  GranularityProperty,
  monthGranularity,
  weekGranularity,
  yearGranularity,
} from "@bitwarden/web-vault/app/models/types/balanceGroupingTypes";
import { addDays, addMonths, addWeeks, addYears, isBefore } from "date-fns";
import { PeriodType } from "@bitwarden/web-vault/app/models/types/filter.types";

export type GroupingSelection = {
  countries: boolean;
  institutions: boolean;
  granularity: GranularityProperty;
  /** True while `granularity` tracks the computed default rather than an explicit user pick. */
  _isDefault: boolean;
};

const initialGrouping: GroupingSelection = {
  countries: false,
  institutions: false,
  granularity: "day",
  _isDefault: true,
};

export type GroupingOptions = {
  countries: [true, false];
  institutions: [true, false];
  granularity: GranularityProperty[];
};

const initialOptions: GroupingOptions = {
  countries: [true, false],
  institutions: [true, false],
  granularity: ["day"],
};

export class GroupControl extends StateControlAbstraction {
  /** Public signal for component to listen too **/
  private _options = signal<GroupingOptions>(initialOptions);
  options = this._options.asReadonly();

  private _selected = signal<GroupingSelection>(initialGrouping);
  selected = this._selected.asReadonly();

  clear() {
    this._options.set(initialOptions);
    this._selected.set(initialGrouping);
  }

  /** **<span style="color: red;">This is for store use only</span>** **/
  readonly selected$ = toObservable(this.selected);

  updateSelection(selection: GroupingSelection) {
    this._selected.set(selection);
  }

  updateOption(option: GroupingOptions) {
    this._options.set(option);
  }

  updateOptionAndSelectionFromPeriod(currentPeriod: PeriodType) {
    const defaultSelection = this.getDefaultGranularityForDates(currentPeriod);
    const options = this.getGranularityOptionsForDates(currentPeriod);

    this._options.set({
      ...this.options(),
      granularity: options,
    });

    // While the user hasn't explicitly picked a granularity, always resync to the correct
    // default for the current period — mirrors PeriodController's `_isDefault` handling. Without
    // this, a stale selection (e.g. "month", carried over from a wider unfiltered view) can
    // silently survive narrowing to a smaller window, because it's still a *valid option* for
    // that window even though it's no longer the *recommended default*.
    if (this.selected()._isDefault) {
      this.updateSelection({
        ...this.selected(),
        granularity: defaultSelection,
        _isDefault: true,
      });

      return;
    }

    // User has manually picked a granularity — only override it if it's no longer valid.
    if (!options?.includes(this.selected().granularity) && options.length > 0) {
      this.updateSelection({
        ...this.selected(),
        granularity: defaultSelection,
        _isDefault: true,
      });
    }
  }

  private getDefaultGranularityForDates(period: PeriodType) {
    for (const granularityDefault of GranularityDefaults) {
      const endDateLimit = this.calculateEndDateLimit(
        period.startDate,
        granularityDefault.limit,
        granularityDefault.limitType,
      );

      if (endDateLimit === null || isBefore(period.endDate, endDateLimit)) {
        return granularityDefault.default;
      }

      if (granularityDefault.limit === null) {
        return granularityDefault.default;
      }
    }

    /** Should never reach here**/
    throw new Error("No default granularity found");
    // return GranularityDefaults[0].default;
  }

  private getGranularityOptionsForDates(period: PeriodType) {
    for (const granularityOption of GranularityOptions) {
      const endDateLimit = this.calculateEndDateLimit(
        period.startDate,
        granularityOption.limit,
        granularityOption.limitType,
      );

      if (endDateLimit === null || isBefore(period.endDate, endDateLimit)) {
        return granularityOption.options;
      }

      if (granularityOption.limit === null) {
        return granularityOption.options;
      }
    }
  }

  private calculateEndDateLimit(startDate: Date, limit: number, limitType: GranularityProperty) {
    switch (limitType) {
      case dayGranularity:
        return addDays(new Date(startDate), limit);
      case weekGranularity:
        return addWeeks(new Date(startDate), limit);
      case monthGranularity:
        return addMonths(new Date(startDate), limit);
      case yearGranularity:
        return addYears(new Date(startDate), limit);
      default:
        return new Date(startDate);
    }
  }
}
